import { Accordion, ChevronIcon, Group, Stack, createStyles, useMantineTheme } from '@mantine/core';
import { z } from 'zod';
import { useCallback, useState, useEffect } from 'react';

import { Text, textStyles } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';

import type { BaseField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import type { APIField } from '../../types/main';
import type { FieldQuery } from '../../../context/types/store';
import { FieldMeta } from '../common/FieldMeta';

import { getDependencyOpenWhen, getDependencyDisabledWhen } from './getDependencyOpenWhen';

export type APIAccordionType = BaseField & {
  type: 'Accordion';
  fields: APIField[];
  isDisabled?: boolean;
  emptyButtonLabel?: string;
  params?: {
    isOpennedByDefault?: boolean;
  };
};

export type AccordionType = Omit<APIAccordionType, 'fields'> & {
  fields: string[];
  heading?: string;
  isOpenOnLoad?: boolean;
  openWhen?: {
    fieldKey: string;
    conditions: Record<string, string>;
  }[];
  disabledWhen?: {
    fieldKey: string;
    conditions: Record<string, string>;
  }[];
};

const useStyles = createStyles(
  (theme, { isError, isDisabled }: { isError: boolean; isDisabled: boolean }) => ({
    item: {
      borderColor: isError ? theme.colors.orange[3] : theme.colors.gray2[0],
      borderRadius: `${theme.other.spacing._8}px !important`,
      pointerEvents: isDisabled ? 'none' : 'auto',
      opacity: isDisabled ? '0.6' : '1',

      '&, &[data-active], &:hover': {
        backgroundColor: theme.white,
      },
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      gap: theme.other.spacing._40,
    },
    control: {
      padding: `${theme.other.spacing._16} ${theme.other.spacing._8}`,
      '&, &:hover': { background: 'transparent' },
    },
    controlContent: {
      display: 'flex',

      [theme.fn.smallerThan('md')]: {
        flexDirection: 'column',
        gap: theme.other.spacing._16,
      },
    },
    heading: {
      width: '150px',

      [theme.fn.smallerThan('md')]: {
        width: '100%',
        flexGrow: 1,
      },
    },
    labels: {
      flex: 1,
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.other.spacing._4,
      alignItems: 'center',
      justifyContent: 'center',

      [theme.fn.smallerThan('md')]: {
        justifyContent: 'start',
      },
    },
    accordionItem: {},
    badge: {
      backgroundColor: theme.colors.gray2[0],
      borderRadius: '100em',
      padding: `${theme.other.spacing._2}px ${theme.other.spacing._12}px`,
      ...textStyles['subhead-medium'],
    },
    chevron: {
      width: '100px',
      justifyContent: 'end',
    },
  })
);

function AccordionElement({
  field,
  sectionKey,
  path,
  FieldSwitch,
  errors,
  isTouched,
  allValues,
}: StaticFieldProps<AccordionType>) {
  const theme = useMantineTheme();
  const [openValues, setOpenValues] = useState<string[]>(field.isOpenOnLoad ? [field.key] : []);
  const [userClosedAccordion, setUserClosedAccordion] = useState(false);
  const [labelsMap, setLabelsMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (field.openWhen && allValues) {
      const conditionFields = field.openWhen.map(
        (condition) => `${sectionKey}.${condition.fieldKey}`
      );

      const shouldOpen = getDependencyOpenWhen(field.openWhen, allValues, sectionKey);

      if (shouldOpen && !userClosedAccordion) {
        setOpenValues([field.key]);
      } else if (
        !shouldOpen &&
        conditionFields.some((fieldPath) => allValues[fieldPath] !== undefined)
      ) {
        setOpenValues([]);
        setUserClosedAccordion(false);
      }
    }
  }, [field.openWhen, field.key, allValues, sectionKey, userClosedAccordion]);

  useEffect(() => {
    if (field.disabledWhen && allValues) {
      const shouldDisable = getDependencyDisabledWhen(field.disabledWhen, allValues, sectionKey);

      if (shouldDisable && openValues.includes(field.key)) {
        setOpenValues([]);
        setUserClosedAccordion(false);
      }
    }
  }, [field.disabledWhen, field.key, allValues, sectionKey, openValues]);

  const setFieldLabel = useCallback((p: string[], label: string) => {
    setLabelsMap((prev) => ({
      ...prev,
      [p.join('.')]: label,
    }));
  }, []);
  const labels = Object.values(labelsMap).filter(Boolean);

  const isDynamicallyDisabled =
    field.disabledWhen && allValues
      ? getDependencyDisabledWhen(field.disabledWhen, allValues, sectionKey)
      : false;

  const isEffectivelyDisabled = field.isDisabled || isDynamicallyDisabled;

  const { classes } = useStyles({
    isError: errors && isTouched,
    isDisabled: isEffectivelyDisabled,
  });

  const handleToggle = (value: string) => {
    const isClosing = openValues.includes(value);
    if (isClosing) {
      setUserClosedAccordion(true);
    }
    setOpenValues(isClosing ? [] : [value]);
  };

  const accordionValue = openValues.length > 0 ? openValues[0] : '';
  const hasNoLabels = labels.length === 0;
  const shouldShowEmptyButton = field?.emptyButtonLabel && hasNoLabels && !accordionValue;

  return (
    <Group>
      <Accordion
        w="100%"
        variant="contained"
        value={accordionValue}
        disableChevronRotation
        onChange={handleToggle}
        classNames={{
          item: classes.item,
          content: classes.content,
          control: classes.control,
          chevron: classes.chevron,
        }}
        styles={
          shouldShowEmptyButton
            ? {
                chevron: {
                  width: 'auto',
                  paddingRight: theme.other.spacing._8,
                },
              }
            : null
        }
        chevron={(() => {
          if (field.isDisabled) {
            return null;
          }

          if (shouldShowEmptyButton) {
            return (
              <Button variant="secondary-fill" size="small">
                {field.emptyButtonLabel}
              </Button>
            );
          }

          return <ChevronIcon />;
        })()}
      >
        <FieldMeta field={field} errors={errors} isTouched={isTouched} />
        <Accordion.Item value={field.key} className={classes.accordionItem}>
          <Accordion.Control>
            <div className={classes.controlContent}>
              <div className={classes.heading}>
                <Text variant="subhead-large">{field.heading}</Text>
              </div>
              <div className={classes.labels}>
                {labels.length > 0
                  ? labels.map((label) => {
                      const selectedLabels = Array.isArray(label);
                      if (selectedLabels) {
                        return label.map((l: string) => (
                          <div className={classes.badge} key={l}>
                            {l}
                          </div>
                        ));
                      }

                      return (
                        <div className={classes.badge} key={label}>
                          {label}
                        </div>
                      );
                    })
                  : null}
              </div>
            </div>
          </Accordion.Control>

          <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={10}>
            <Accordion.Panel>
              {field.fields.map((key) => (
                <FieldSwitch
                  key={key}
                  fieldKey={key}
                  sectionKey={sectionKey}
                  path={path}
                  propagateFieldLabelToParent={setFieldLabel}
                />
              ))}
            </Accordion.Panel>
          </Stack>
        </Accordion.Item>
      </Accordion>
    </Group>
  );
}

const Descriptor: FieldDescriptor<APIAccordionType, AccordionType> = {
  FieldComponent: AccordionElement,
  name: 'Accordion',
  parseFieldResponse: ({ field, fieldKey, parse }) => {
    const fields = field.fields
      .map((childField) => parse(childField, `${fieldKey}.`))
      .filter(Boolean);

    const normalizedField: FieldQuery = {
      key: fieldKey,
      type: field.type,
      data: {
        ...field,
        fields: fields.map((f) => f[0].key),
      },
    };

    return [normalizedField, fields];
  },
  createValidationSchema: (field, create) =>
    z.object(
      field.fields.reduce((acc, fieldKey) => {
        const result = create(fieldKey);
        if (result) {
          if ('field' in result) {
            const [key, schema] = result.field;
            acc[key] = schema;
          } else {
            result.fields.forEach(([key, schema]) => {
              acc[key] = schema;
            });
          }
        }

        return acc;
      }, {})
    ),
};

export default Descriptor;
